import * as THREE from "three";
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import { UNIT_BOX } from "../../engines/geometry";
import type { BuildInput } from "../../engines/types";
import {
	matDoorHandle,
	matGuideDoor,
	matGuideStud,
	matGuideWireframe,
} from "../../engines/materials";
import { disposeGroupGeometries, GUIDE_SPHERE } from "./sceneDisposal";

export function syncOpeningGuides(
	group: THREE.Group,
	buildInput: BuildInput,
	showOpeningGuides: boolean,
): void {
	const existing = group.getObjectByName("detail/opening-guides");
	if (existing) {
		group.remove(existing);
		disposeGroupGeometries(existing);
	}
	if (!showOpeningGuides) return;

	const { dimensions, openings } = buildInput;
	if (openings.length === 0) return;
	const guides = new THREE.Group();
	guides.name = "detail/opening-guides";

	const W = dimensions.width;
	const L = dimensions.length;
	const wallBaseY = 0.165;

	const addGuideBox = (
		sx: number,
		sy: number,
		sz: number,
		px: number,
		py: number,
		pz: number,
		mat: THREE.Material,
	) => {
		const mesh = new THREE.Mesh(UNIT_BOX, mat);
		mesh.scale.set(sx, sy, sz);
		mesh.position.set(px, py, pz);
		guides.add(mesh);
		return mesh;
	};

	const addGuideHandle = (px: number, py: number, pz: number) => {
		const handle = new THREE.Mesh(GUIDE_SPHERE, matDoorHandle);
		handle.scale.setScalar(0.02);
		handle.position.set(px, py, pz);
		handle.castShadow = true;
		handle.receiveShadow = true;
		guides.add(handle);
	};

	const placeOpening = (
		opening: WallOpening,
		wallZ: number,
		isBack = false,
	) => {
		const widthM = opening.widthMm / 1000;
		const heightM = opening.heightMm / 1000;
		const xM = -W / 2 + opening.xMm / 1000 + widthM / 2;
		const yM = wallBaseY + opening.yMm / 1000 + heightM / 2;
		const isDoor = opening.type === "door";
		const panelMat = isDoor ? matGuideDoor : matGuideWireframe;

		addGuideBox(
			widthM,
			heightM,
			0.04,
			xM,
			yM,
			wallZ + (isBack ? 0.02 : -0.02),
			panelMat,
		);

		if (isDoor) {
			addGuideHandle(
				xM + widthM / 2 - 0.06,
				yM,
				wallZ + (isBack ? 0.05 : -0.05),
			);
		}

		addGuideBox(0.04, heightM, 0.04, xM - widthM / 2, yM, wallZ, matGuideStud);
		addGuideBox(0.04, heightM, 0.04, xM + widthM / 2, yM, wallZ, matGuideStud);
		addGuideBox(
			widthM + 0.08,
			0.04,
			0.04,
			xM,
			wallBaseY + opening.yMm / 1000 + heightM,
			wallZ,
			matGuideStud,
		);
	};

	const placeSideOpening = (opening: WallOpening, wallX: number) => {
		const widthM = opening.widthMm / 1000;
		const heightM = opening.heightMm / 1000;
		const zM = -L / 2 + opening.xMm / 1000 + widthM / 2;
		const yM = wallBaseY + opening.yMm / 1000 + heightM / 2;
		const isDoor = opening.type === "door";
		const panelMat = isDoor ? matGuideDoor : matGuideWireframe;

		addGuideBox(0.04, heightM, widthM, wallX, yM, zM, panelMat);

		if (isDoor) {
			addGuideHandle(wallX, yM, zM + widthM / 2 - 0.06);
		}

		addGuideBox(0.04, heightM, 0.04, wallX, yM, zM - widthM / 2, matGuideStud);
		addGuideBox(0.04, heightM, 0.04, wallX, yM, zM + widthM / 2, matGuideStud);
		addGuideBox(
			0.04,
			0.04,
			widthM + 0.08,
			wallX,
			wallBaseY + opening.yMm / 1000 + heightM,
			zM,
			matGuideStud,
		);
	};

	for (const opening of openings) {
		if (opening.wall === "front") {
			placeOpening(opening, L / 2 - 0.095, false);
		} else if (opening.wall === "back") {
			placeOpening(opening, -L / 2 + 0.095, true);
		} else if (opening.wall === "left") {
			placeSideOpening(opening, -W / 2 + 0.095);
		} else if (opening.wall === "right") {
			placeSideOpening(opening, W / 2 - 0.095);
		}
	}

	group.add(guides);
}
